import fetcher from 'api/fetcher';
import axios, { type AxiosError } from 'axios';
import { CampaignProductType, CampaignType, VacancyError } from 'utils/constants/campaign';
import { MagritteNotificationName } from 'utils/constants/notification';

import {
    ErrorType,
    targetErrorElementShownRaw,
} from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/target_error_element_shown';

import { setIsVacancyForeignArea } from 'store/autoReducers';
import {
    AuctionProductData,
    CpcProductData,
    setWizardCampaignOptions,
    setWizardSelectedVacancy,
    setWizardWebsiteUrlErrorKey,
} from 'store/models/campaignCreationWizard';
import { resetMainMenuFormData, setMainMenuLink } from 'store/models/mainMenu';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

interface BannerLinkCheckResponse {
    vacancyId: number | null;
    advertisingServiceType: CampaignType;
    vacancyCampaignOptions?: {
        vacancyName: string;
        auctionProductData: AuctionProductData;
        cpcProductData: CpcProductData;
        productTypes: CampaignProductType[];
    };
    foreignArea?: boolean;
}

interface BannerLinkCheckError {
    errors: { key: VacancyError }[];
}

export type WizardBannerLinkResult =
    | { isVacancyUrl: true }
    | { isVacancyUrl: false; advertisingServiceType: CampaignType }
    | null;

const VACANCY_BANNER_LINK_ERROR_KEY_MAP: Record<VacancyError, string> = {
    [VacancyError.InvalidUrlError]: 'CampaignCreationWizard.VacancySelectStep.website.errorInvalidUrl',
    [VacancyError.VacancyNotFoundError]: 'errorVacancyInBannerLink.notification.text',
    [VacancyError.IllegalVacancyStatusError]: 'errorVacancyInBannerLink.notification.text',
};

export const fetchWizardBannerLinkCheck =
    (websiteUrl: string, abortSignal?: AbortSignal): AppThunkAction<Promise<WizardBannerLinkResult>> =>
    (dispatch) => {
        return fetcher
            .put<unknown, BannerLinkCheckResponse>(
                '/api/v1/campaigns/advertising_service_type_extended',
                {
                    url: websiteUrl,
                },
                {
                    signal: abortSignal,
                }
            )
            .then(({ vacancyId, advertisingServiceType, vacancyCampaignOptions, foreignArea }) => {
                if (vacancyId) {
                    dispatch([
                        setWizardSelectedVacancy({
                            vacancyId,
                            vacancyName: vacancyCampaignOptions?.vacancyName,
                            foreignArea: Boolean(foreignArea),
                        }),
                        setIsVacancyForeignArea(Boolean(foreignArea)),
                        resetMainMenuFormData(null),
                    ]);
                    if (vacancyCampaignOptions) {
                        dispatch(setWizardCampaignOptions({ vacancyId, campaignOptions: vacancyCampaignOptions }));
                    }
                    return { isVacancyUrl: true } as WizardBannerLinkResult;
                }
                dispatch([
                    setWizardSelectedVacancy(null),
                    setIsVacancyForeignArea(false),
                    resetMainMenuFormData(null),
                    setMainMenuLink(websiteUrl),
                ]);
                return { isVacancyUrl: false, advertisingServiceType } as WizardBannerLinkResult;
            })
            .catch((err: AxiosError<BannerLinkCheckError>) => {
                if (axios.isCancel(err) || abortSignal?.aborted) {
                    return null;
                }
                if (err?.response?.status === 400) {
                    const key = err.response.data.errors?.[0].key;
                    targetErrorElementShownRaw({ errorType: key as ErrorType });
                    dispatch(
                        setWizardWebsiteUrlErrorKey(
                            VACANCY_BANNER_LINK_ERROR_KEY_MAP[key] ??
                                'CampaignCreationWizard.VacancySelectStep.website.errorInvalidUrl'
                        )
                    );
                } else if (err?.response?.status === 403) {
                    const key = err.response.data.errors?.[0].key;
                    targetErrorElementShownRaw({ errorType: key as ErrorType });
                    dispatch(
                        setWizardWebsiteUrlErrorKey('CampaignCreationWizard.VacancySelectStep.website.errorAccess')
                    );
                } else {
                    dispatch(addMagritteNotification(MagritteNotificationName.SomethingWentWrong));
                }
                throw err;
            });
    };
